import React, { useState, useEffect } from "react";

import MakeAPICall from "../../apiCalls/common";

import {
  getReceivedResultsApiRoute,
  deleteReceivedResultApiRoute,
  approveReceivedResultApiRoute
} from "../../apiRoutes";

import { getFormattedDate } from "../../utils/dateTimeHelper";
import { getMappedStatus } from "../../utils/commonMethods";

import Loader from "../../components/loader";
import CustomModal from "../../components/modal";
import CustomDataTable from "../../components/datatable/customDataTable";
import PatientSearch from "../../containers/Tickets/patientSearch";

const renderList = items => {
  if (!items || items.length === 0) {
    return <p className="mb-0">-</p>;
  }

  return (
    <ul className="mb-0 pl-3">
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  );
};

const TicketDetailView = ({ ticketDetail }) => (
  <div className="row">
    <div className="col-md-6 mb-3">
      <label className="mb-0 text-muted">Ticket Number</label>
      <p>{ticketDetail.caseNumber || "-"}</p>
    </div>
    <div className="col-md-6 mb-3">
      <label className="mb-0 text-muted">Status</label>
      <p>{getMappedStatus(ticketDetail.status)}</p>
    </div>
    <div className="col-md-6 mb-3">
      <label className="mb-0 text-muted">Date Received</label>
      <p>{ticketDetail.dateReceived ? getFormattedDate(ticketDetail.dateReceived) : "-"}</p>
    </div>
    <div className="col-md-6 mb-3">
      <label className="mb-0 text-muted">Follow Up</label>
      <p>{ticketDetail.followUpDate ? getFormattedDate(ticketDetail.followUpDate) : "-"}</p>
    </div>
    <div className="col-md-6 mb-3">
      <label className="mb-0 text-muted">Doctor</label>
      <p>{ticketDetail.doctor}</p>
    </div>
    <div className="col-md-6 mb-3">
      <label className="mb-0 text-muted">Patient</label>
      <p>{ticketDetail.patient}</p>
    </div>
    <div className="col-md-12 mb-3">
      <label className="mb-0 text-muted">Subject</label>
      <p>{ticketDetail.subject || "-"}</p>
    </div>
    <div className="col-md-6 mb-3">
      <label className="mb-0 text-muted">Requested Tests</label>
      {renderList(ticketDetail.requestedTests)}
    </div>
    <div className="col-md-6 mb-3">
      <label className="mb-0 text-muted">Results Received</label>
      {renderList(ticketDetail.resultsReceived)}
    </div>
    <div className="col-md-6 mb-3">
      <label className="mb-0 text-muted">Refused Tests</label>
      {renderList(ticketDetail.refusedTests)}
    </div>
  </div>
);

const tableHeaders = [
  {
    key: "caseNumber",
    value: "Ticket Number",
    customStyle: { maxWidth: "100px" }
  },
  { key: "doctor", value: "Doctor" },
  { key: "patient", value: "Patient" },
  { key: "matchScore", value: "Match Score" },
  { key: "documents", value: "Documents", sort: false },
  { key: "action", value: "Action", sort: false }
];

// match score is 0-50 in increments of 10, shown as 5 dots
// dot thresholds: 10 = red-orange, 20 = orange, 30 = yellow, 40 = light green, 50 = dark green
// (score 0 means no dots are filled, all shown gray)
const MATCH_SCORE_DOT_COLORS = ["#e8590c", "#ef9f27", "#f1c40f", "#7bc96f", "#1e7e34"];

const MatchScoreDots = ({ score }) => {
  const filledDots = Math.max(0, Math.min(5, Math.round((score || 0) / 10)));

  return (
    <div style={{ display: "flex", gap: "4px" }}>
      {MATCH_SCORE_DOT_COLORS.map((color, i) => (
        <span
          key={i}
          style={{
            width: "12px",
            height: "12px",
            borderRadius: "50%",
            backgroundColor: i < filledDots ? color : "#e0e0e0"
          }}
        ></span>
      ))}
    </div>
  );
};

// derive the result type from the ticket subject (e.g. "Radiology Request Created" -> RadiologyResults)
const getResultType = subject => {
  if (subject && subject.toLowerCase().includes("pathology")) {
    return "PathologyResults";
  } else if (subject && subject.toLowerCase().includes("radiology")) {
    return "RadiologyResults";
  }
  return undefined;
};

const RESULT_TYPE_LABELS = {
  PathologyResults: "Pathology Report",
  RadiologyResults: "Radiology Report"
};

const getDocumentLabel = (resultType, index, total) => {
  const label = RESULT_TYPE_LABELS[resultType] || "Document";
  return total > 1 ? `${label} ${index + 1}.pdf` : `${label}.pdf`;
};

const formatPersonName = person =>
  [person.firstName, person.middleName, person.lastName]
    .filter(part => part && part.trim().length > 0)
    .join(" ");

const mapResultItem = item => {
  const patient = item.patientId || {};
  const doctor = item.doctorId || {};
  const ticket = item.ticketId || {};
  const ticketDoctor = ticket.doctorId || {};
  const ticketPatient = ticket.patientId || {};

  const patientName = formatPersonName(patient);
  const doctorName = formatPersonName(doctor);
  const ticketDoctorName = formatPersonName(ticketDoctor);
  const ticketPatientName = formatPersonName(ticketPatient);

  const documentUrls = item.attachedDocuments || [];
  const resultType = getResultType(ticket.subject);

  return {
    id: item._id,
    ticketId: ticket._id,
    caseNumber: ticket.caseNumber || "-",
    doctor: doctorName ? `UIN: ${doctor.doctorId} | ${doctorName}` : "-",
    patient: patientName ? `UIN: ${patient.patientId} | ${patientName}` : "-",
    patientName: patientName,
    suggestedPatient: patient._id ? { id: patient._id, patientId: patient.patientId, firstName: patient.firstName, lastName: patient.lastName } : null,
    matchScore: item.matchScore,
    documents: documentUrls.map((url, i) => ({
      url,
      type: resultType,
      label: getDocumentLabel(resultType, i, documentUrls.length)
    })),
    ticketDetail: {
      caseNumber: ticket.caseNumber || "-",
      status: ticket.status,
      dateReceived: ticket.dateReceived,
      followUpDate: ticket.followUpDate,
      subject: ticket.subject,
      requestedTests: ticket.requestedTests,
      resultsReceived: ticket.resultsReceived,
      refusedTests: ticket.refusedTests,
      doctor: ticketDoctorName ? `UIN: ${ticketDoctor.doctorId} | ${ticketDoctorName}` : "-",
      patient: ticketPatientName ? `UIN: ${ticketPatient.patientId} | ${ticketPatientName}` : "-"
    }
  };
};

const ReceivedResultsPage = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [apiCallParams, setApiCallParams] = useState(null);
  const [receivedResults, setReceivedResults] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currFilters, setCurrFilters] = useState({
    skip: 0,
    limit: 10
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showTicketDetailModal, setShowTicketDetailModal] = useState(false);
  const [currSelResult, setCurrSelResult] = useState(null);
  const [approvePatient, setApprovePatient] = useState(null);
  const [approvePatientErr, setApprovePatientErr] = useState("");

  const getReceivedResults = () => {
    setIsProcessing(true);

    setApiCallParams({
      reqType: "GET",
      reqUrl: getReceivedResultsApiRoute,
      params: currFilters,
      successCallback: res => {
        setIsProcessing(false);
        const data = res.data.data;
        setReceivedResults((data.ticketPatientMapping || []).map(mapResultItem));
        setTotalRecords(data.totalCount || 0);
      },
      failureCallback: () => {
        setIsProcessing(false);
      }
    });
  };

  useEffect(() => {
    getReceivedResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currFilters]);

  const setTableFilters = filters => {
    let newFilter = Object.assign({}, currFilters);
    newFilter.limit = filters.limit;
    newFilter.skip = filters.skip;
    setCurrFilters(newFilter);
  };

  const deleteResult = () => {
    setShowDeleteModal(false);
    setIsProcessing(true);

    setApiCallParams({
      reqType: "POST",
      reqUrl: deleteReceivedResultApiRoute,
      dataObj: { autoTicketPatientMappingId: currSelResult.id },
      successCallback: () => {
        setCurrSelResult(null);
        getReceivedResults();
      },
      failureCallback: () => {
        setIsProcessing(false);
        setCurrSelResult(null);
      }
    });
  };

  const approveResult = () => {
    if (!approvePatient || !approvePatient.id) {
      setApprovePatientErr("Please search and select a patient before approving");
      return;
    }
    setApprovePatientErr("");
    setShowApproveModal(false);
    setIsProcessing(true);

    setApiCallParams({
      reqType: "POST",
      reqUrl: approveReceivedResultApiRoute,
      dataObj: {
        autoTicketPatientMappingId: currSelResult.id,
        ticketId: currSelResult.ticketId,
        patientId: approvePatient.id,
        attachments: currSelResult.documents.map(doc => ({
          url: doc.url,
          documentType: doc.type
        }))
      },
      successCallback: () => {
        setCurrSelResult(null);
        setApprovePatient(null);
        getReceivedResults();
      },
      failureCallback: () => {
        setIsProcessing(false);
        setCurrSelResult(null);
        setApprovePatient(null);
      }
    });
  };

  return (
    <div className="view-ticket-container">
      {isProcessing && <Loader />}
      <div className={isProcessing ? "disableContainer" : ""}>
        <CustomModal
          title="Instant Consult"
          okayText="Yes"
          cancelText="No"
          openModal={showDeleteModal}
          cancelEvent={() => {
            setShowDeleteModal(false);
            setCurrSelResult(null);
          }}
          okayEvent={deleteResult}
          modalBodyContent={
            <p className="text-center mb-0">
              Are you sure you want to delete this result?
            </p>
          }
        />
        <CustomModal
          title="Approve Result"
          okayText="Approve"
          cancelText="Cancel"
          className="modal-lg"
          openModal={showApproveModal}
          cancelEvent={() => {
            setShowApproveModal(false);
            setCurrSelResult(null);
            setApprovePatient(null);
            setApprovePatientErr("");
          }}
          okayEvent={approveResult}
          modalBodyContent={
            currSelResult && (
              <div className="row">
                <div className="col-md-6">
                  <TicketDetailView ticketDetail={currSelResult.ticketDetail} />
                </div>
                <div className="col-md-6 border-left">
                  <label className="mb-0 text-muted">Suggested Patient</label>
                  <p>{currSelResult.patient}</p>
                  <PatientSearch
                    key={currSelResult.id}
                    initialName={currSelResult.patientName}
                    onSelectPatient={patient => {
                      setApprovePatient(patient);
                      setApprovePatientErr("");
                    }}
                  />
                  {approvePatientErr && <p className="error mt-2">{approvePatientErr}</p>}
                  {approvePatient &&
                    (!currSelResult.suggestedPatient || approvePatient.id !== currSelResult.suggestedPatient.id) && (
                      <div className="mt-3">
                        <label className="mb-0 text-muted">Selected Patient</label>
                        <p className="mb-0">
                          UIN: {approvePatient.patientId} | {approvePatient.firstName} {approvePatient.lastName}
                        </p>
                      </div>
                    )}
                </div>
              </div>
            )
          }
        />
        <CustomModal
          title="Ticket Detail"
          useOneButton={true}
          cancelText="Close"
          className="modal-lg"
          openModal={showTicketDetailModal}
          cancelEvent={() => {
            setShowTicketDetailModal(false);
            setCurrSelResult(null);
          }}
          modalBodyContent={currSelResult && <TicketDetailView ticketDetail={currSelResult.ticketDetail} />}
        />
        <div className="d-flex flex-row flex-wrap justify-content-between">
          <h3>Received Results</h3>
        </div>
        <CustomDataTable
          totalRecords={totalRecords}
          headers={tableHeaders}
          setFilters={setTableFilters}
          sortableTable={false}
          tabFilters={currFilters}
        >
          {receivedResults.map((item, index) => (
            <tr key={index} className="row-no-hover-effect received-results-row">
              <td style={{ maxWidth: "100px" }}>
                <a
                  href="#!"
                  className="received-results-doc-link"
                  onClick={e => {
                    e.preventDefault();
                    setCurrSelResult(item);
                    setShowTicketDetailModal(true);
                  }}
                >
                  {item.caseNumber}
                </a>
              </td>
              <td>{item.doctor}</td>
              <td>{item.patient}</td>
              <td>
                <MatchScoreDots score={item.matchScore} />
              </td>
              <td>
                {item.documents.map((doc, docIndex) => (
                  <a
                    key={docIndex}
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="d-block received-results-doc-link"
                  >
                    {doc.label}
                  </a>
                ))}
              </td>
              <td>
                <div className="d-flex flex-row align-items-center">
                  <button
                    type="button"
                    className="btn btn-out-green ml-2"
                    onClick={e => {
                      e.stopPropagation();
                      setCurrSelResult(item);
                      setApprovePatient(item.suggestedPatient);
                      setShowApproveModal(true);
                    }}
                  >
                    <i className="fas fa-check"></i>
                  </button>
                  <button
                    type="button"
                    className="btn btn-out-delete ml-2"
                    onClick={e => {
                      e.stopPropagation();
                      setCurrSelResult(item);
                      setShowDeleteModal(true);
                    }}
                  >
                    <i className="fas fa-trash-alt"></i>
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </CustomDataTable>
        {/* API Call */}
        <MakeAPICall apiParam={apiCallParams} />
      </div>
    </div>
  );
};

export default ReceivedResultsPage;
